import React from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import {
  fetchWarehouseTransfersPage,
  viewerTimeZone,
  warehouseQueryKeys,
  type WarehouseTransferRow,
  type WarehouseTransfersParams,
} from '@/data/warehouse';
import { ALL_SITE_FILTER, summarizeSiteFilterSelection } from '@/domain/library';
import { formatCount, formatDay, formatDayRange, formatRelativeTime, pluralize } from '@/domain/warehouse';
import SearchableMultiSelect from '../../../components/SearchableMultiSelect';
import StageFilterToolbar from '../../../components/events/StageFilterToolbar';
import type { GroupedTableColumn } from '../../../components/common/GroupedDataTable';
import LibraryRecordCard, { LibraryRecordCardDetail } from '../../../components/common/LibraryRecordCard';
import MaintenanceActionsDropdown, { type MaintenanceAction } from '../../../components/admin/MaintenanceActionsDropdown';
import { LateDate, SourceTag, StatusChip } from '../../../components/status/StatusChip';
import LibraryListToolbar, { type LibraryListSortOption } from '../../../components/library/LibraryListToolbar';
import LibraryListBody from '../../../components/library/LibraryListBody';
import WarehouseHero, { type WarehouseHeroStat } from './WarehouseHero';
import WarehouseMeter from './WarehouseMeter';
import TransfersCalendarView from './TransfersCalendarView';
import { useWarehouseListState, type WarehouseListConfig, type WarehouseViewMode } from './useWarehouseListState';
import { buildStatusFilterModel } from './warehouseStatusFilters';
import { FOLD_TAIL, foldAgeSentence, warehouseCountText } from './warehouseListText';
import { warehouseRecordHref, type TransferDirection } from './warehouseTabs';
import { WAREHOUSE_PAGE_SIZE, type WarehouseTabContext } from './shared';

const STATUS_MODEL = buildStatusFilterModel('transfer');
const VIEWS: readonly WarehouseViewMode[] = ['list', 'calendar', 'grid'];
const VIEW_OPTIONS = [
  { value: 'list' as const, label: 'List' },
  { value: 'calendar' as const, label: 'Calendar' },
  { value: 'grid' as const, label: 'Grid' },
];
const FACET_OPTIONS = [
  { value: 'both', label: 'Both ways' },
  { value: 'out', label: 'Shipping out' },
  { value: 'in', label: 'Coming in' },
];
const SORT_OPTIONS: readonly LibraryListSortOption[] = [
  { value: 'ship', label: 'Ship date' },
  { value: 'arrive', label: 'Arrival date' },
  { value: 'doc', label: 'Transfer number' },
  { value: 'status', label: 'Status' },
  { value: 'updated', label: 'Last update' },
];
const DESCENDING_FIRST = ['updated'];
const NOUN = { singular: 'transfer', plural: 'transfers' };
/** Statuses with something on the way, so the row menu offers Receive (Portal transfers only). */
const RECEIVABLE = new Set(['partially_shipped', 'shipped', 'in_transit', 'partially_received']);

export const TRANSFERS_DEFAULT_CONFIG: WarehouseListConfig = {
  search: '',
  sites: [ALL_SITE_FILTER],
  facet: 'both',
  statuses: STATUS_MODEL.defaults,
  chips: [],
  grouping: 'date',
  fold: true,
  sortKey: 'ship',
  sortDir: 'asc',
  view: 'list',
  dateFrom: '',
  dateTo: '',
  secondDateFrom: '',
  secondDateTo: '',
  vendorIds: [],
  orderedBy: '',
};

type TransfersTabProps = {
  context: WarehouseTabContext;
  /** From a legacy `?tab=outbound|inbound|in_transit|completed` link; applied once. */
  legacy: { direction?: TransferDirection; statuses?: string[] } | null;
  onLegacyApplied: () => void;
  onNewTransfer: () => void;
  onScanToReceive?: () => void;
};

function transferContents(row: WarehouseTransferRow): { main: string; sub: React.ReactNode } {
  const main = `${pluralize(row.lineCount, 'line')} · ${pluralize(row.units, 'unit')}`;
  if (row.status === 'partially_picked' || (row.status === 'picked' && row.pickedUnits < row.units)) {
    return { main, sub: <WarehouseMeter done={row.pickedUnits} total={row.units} suffix="picked" /> };
  }
  if (row.status === 'partially_received') {
    return { main, sub: <WarehouseMeter done={row.receivedUnits} total={row.units} suffix="received" /> };
  }
  if (row.serviceTicket?.docNo) return { main, sub: `Service ticket ${row.serviceTicket.docNo}` };
  if (row.firstItemName) {
    return { main, sub: row.lineCount > 1 ? `${row.firstItemName} + ${row.lineCount - 1} more` : row.firstItemName };
  }
  return { main, sub: null };
}

function transferReturnNote(row: WarehouseTransferRow): string | null {
  if (row.scope === 'permanent') return 'Permanent, no return';
  if (row.serviceTicket) return 'Returns after repair';
  const due = formatDay(row.returnDueDay);
  if (due && row.returnLeg?.docNo) return `Returns ${due} · ${row.returnLeg.docNo}`;
  if (due) return `Returns ${due}`;
  if (row.returnLeg?.docNo) return `Return leg ${row.returnLeg.docNo}`;
  return null;
}

export default function TransfersTab({ context, legacy, onLegacyApplied, onNewTransfer, onScanToReceive }: TransfersTabProps) {
  const navigate = useNavigate();
  const [overrides] = React.useState<Partial<WarehouseListConfig> | null>(() => {
    if (!legacy) return null;
    const next: Partial<WarehouseListConfig> = {};
    if (legacy.direction) next.facet = legacy.direction;
    if (legacy.statuses) next.statuses = legacy.statuses;
    return Object.keys(next).length > 0 ? next : null;
  });
  // The preset belongs to the link that opened the page; coming back to this tab later keeps
  // whatever the viewer changed since.
  React.useEffect(() => {
    if (legacy) onLegacyApplied();
  }, [legacy, onLegacyApplied]);

  const state = useWarehouseListState({
    tab: 'transfers',
    profileId: context.profileId,
    prefs: context.prefs,
    prefsLoaded: context.prefsLoaded,
    defaults: TRANSFERS_DEFAULT_CONFIG,
    overrides,
    views: VIEWS,
    allStatuses: STATUS_MODEL.statuses,
    siteOptionIds: context.siteOptionIds,
    regions: context.regionFilterOptions,
    scopeKey: context.linkedEvent?.id ?? null,
    descendingFirst: DESCENDING_FIRST,
  });
  const { config, update, page, setPage, sortFor, setGrouping } = state;
  const [advancedOpen, setAdvancedOpen] = React.useState(false);

  const pastShipChip = config.chips.includes('past_ship_date');
  const params = React.useMemo<WarehouseTransfersParams>(() => ({
    search: state.debouncedSearch,
    locationIds: state.locationIds,
    statuses: state.statusesParam,
    chips: config.chips,
    eventId: context.linkedEvent?.id ?? null,
    includeFold: !config.fold || state.showFolded || pastShipChip,
    groupBy: config.grouping,
    sortKey: config.sortKey,
    sortDir: config.sortDir,
    offset: (page - 1) * WAREHOUSE_PAGE_SIZE,
    limit: WAREHOUSE_PAGE_SIZE,
    timezone: viewerTimeZone(),
    direction: config.facet === 'out' || config.facet === 'in' ? config.facet : 'both',
    shipFrom: config.dateFrom || null,
    shipTo: config.dateTo || null,
    createdFrom: config.secondDateFrom || null,
    createdTo: config.secondDateTo || null,
  }), [config, context.linkedEvent, page, pastShipChip, state.debouncedSearch, state.locationIds, state.showFolded, state.statusesParam]);

  const isCalendar = config.view === 'calendar';
  const pageQuery = useQuery({
    queryKey: warehouseQueryKeys.transfers(params),
    queryFn: () => fetchWarehouseTransfersPage(params),
    enabled: !state.noStatuses,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
  const data = state.noStatuses ? undefined : pageQuery.data;
  const stats = data?.stats ?? {};
  const chipCounts = data?.chipCounts ?? {};
  const total = data?.total ?? 0;
  const loading = !state.noStatuses && pageQuery.isLoading && !data;
  const statValue = (key: string) => (data ? formatCount(stats[key] ?? 0) : '…');

  const heroStats: WarehouseHeroStat[] = [
    {
      key: 'shipping',
      label: 'Shipping in the next 2 weeks',
      value: statValue('shipping_next_14_days'),
      hint: data ? `${formatCount(stats.shipping_next_14_days_drafts ?? 0)} still in draft` : null,
    },
    {
      key: 'in_transit',
      label: 'In transit',
      value: statValue('in_transit'),
      hint: data ? `${formatCount(stats.in_transit_late ?? 0)} past their arrival date` : null,
      tone: (stats.in_transit_late ?? 0) > 0 ? 'warn' : null,
    },
    {
      key: 'partially_received',
      label: 'Partially received',
      value: statValue('partially_received'),
      hint: 'Shortages to resolve',
    },
    {
      key: 'past_ship_date',
      label: 'Past ship date',
      value: statValue('past_ship_date'),
      hint: 'Confirmed, no shipment recorded',
      tone: (stats.past_ship_date ?? 0) > 0 ? 'warn' : null,
    },
  ];

  const handleGroupingChange = React.useCallback(function handleGroupingChange(event: React.ChangeEvent<HTMLSelectElement>) {
    setGrouping(event.target.value);
  }, [setGrouping]);
  const handleSortChange = React.useCallback(function handleSortChange(sortKey: string, sortDir: 'asc' | 'desc') {
    update({ sortKey, sortDir });
  }, [update]);

  const rowActions = React.useCallback((row: WarehouseTransferRow): MaintenanceAction[] => {
    const href = warehouseRecordHref.transfer(row.id);
    const actions: MaintenanceAction[] = [{ key: 'open', label: 'Open transfer', onSelect: () => navigate(href) }];
    if (row.source === 'portal' && RECEIVABLE.has(row.status)) {
      actions.push({
        key: 'receive',
        label: 'Receive items',
        onSelect: () => navigate(`${warehouseRecordHref.transfer(row.id, 'lines')}?receive=1`),
      });
    }
    return actions;
  }, [navigate]);
  const renderMenu = (row: WarehouseTransferRow) => (
    <MaintenanceActionsDropdown menuOnly menuLabel={`Actions for ${row.docNo ?? 'transfer'}`} actions={rowActions(row)} />
  );

  const columns: GroupedTableColumn<WarehouseTransferRow>[] = [
    {
      key: 'status',
      header: 'Status',
      width: '9.5rem',
      sort: sortFor('status'),
      render: (row) => <StatusChip kind="transfer" status={row.status} />,
    },
    {
      key: 'transfer',
      header: 'Transfer',
      sort: sortFor('doc'),
      render: (row) => (
        <>
          <span className="library-list-doc">
            <Link className="library-list-doc__link" to={warehouseRecordHref.transfer(row.id)}>{row.docNo ?? 'Transfer'}</Link>
            {row.source === 'r2' ? <SourceTag source="r2" /> : null}
          </span>
          {row.description ? <span className="grouped-data-table__sub" title={row.description}>{row.description}</span> : null}
        </>
      ),
    },
    {
      key: 'route',
      header: 'Route',
      render: (row) => (
        <>
          <span>{row.fromSite?.name ?? '—'}</span>
          <span className="grouped-data-table__sub warehouse-route__to">
            <span aria-hidden="true">{'→ '}</span>
            <span className="sr-only">to </span>
            {row.toSite?.name ?? '—'}
          </span>
        </>
      ),
    },
    {
      key: 'dates',
      header: 'Ship → arrive',
      width: '10.5rem',
      sort: sortFor('ship'),
      render: (row) => {
        const note = transferReturnNote(row);
        return (
          <>
            <span className="num">{formatDayRange(row.shipDay, row.arriveDay) ?? 'No date'}</span>
            {row.daysLate ? <> <LateDate daysLate={row.daysLate} /></> : null}
            {note ? <span className="grouped-data-table__sub">{note}</span> : null}
          </>
        );
      },
    },
    {
      key: 'contents',
      header: 'Contents',
      render: (row) => {
        const contents = transferContents(row);
        return (
          <>
            <span>{contents.main}</span>
            {contents.sub ? <span className="grouped-data-table__sub">{contents.sub}</span> : null}
          </>
        );
      },
    },
    {
      key: 'event',
      header: 'Event',
      render: (row) => (row.event ? (
        <>
          <Link className="library-list-doc__link" to={warehouseRecordHref.event(row.event.id)}>{row.event.code ?? 'Event'}</Link>
          <span className="grouped-data-table__sub">
            {row.event.name}
            {row.eventCount > 1 ? ` + ${row.eventCount - 1} more` : ''}
          </span>
        </>
      ) : <span className="fg-muted">{'—'}</span>),
    },
    {
      key: 'updated',
      header: 'Updated',
      width: '7rem',
      optional: true,
      sort: sortFor('updated'),
      render: (row) => <span className="fg-muted">{formatRelativeTime(row.lastActivityAt) ?? '—'}</span>,
    },
    {
      key: 'menu',
      header: <span className="sr-only">Actions</span>,
      width: '3rem',
      className: 'grouped-data-table__menu-cell',
      render: renderMenu,
    },
  ];

  const serverFold = data?.fold ?? null;
  const foldCopy = config.fold && !pastShipChip && serverFold ? {
    title: 'Past ship date',
    body: [
      `${formatCount(serverFold.count)} confirmed R2 transfers have a ship date in the past and no shipment recorded.`,
      foldAgeSentence(serverFold),
      FOLD_TAIL,
    ].filter(Boolean).join(' '),
  } : null;

  const openOnly = !state.statusFilterActive;
  const countText = isCalendar
    ? (loading ? 'Loading transfers…' : `${formatCount(total)} ${openOnly ? 'open ' : ''}${total === 1 ? 'transfer' : 'transfers'} match`)
    : warehouseCountText({ loading, page, pageSize: WAREHOUSE_PAGE_SIZE, total, noun: NOUN, openOnly, fold: serverFold });

  const renderCard = (row: WarehouseTransferRow) => {
    const contents = transferContents(row);
    const href = warehouseRecordHref.transfer(row.id);
    return (
      <LibraryRecordCard
        key={row.id}
        href={href}
        onOpen={() => navigate(href)}
        code={row.docNo ?? 'Transfer'}
        badges={(
          <>
            {row.source === 'r2' ? <SourceTag source="r2" className="library-record-card__pill" /> : null}
            <StatusChip kind="transfer" status={row.status} size="sm" className="library-record-card__pill" />
          </>
        )}
        title={row.description || `${row.fromSite?.name ?? '—'} → ${row.toSite?.name ?? '—'}`}
        details={(
          <>
            <LibraryRecordCardDetail>{`${row.fromSite?.name ?? '—'} → ${row.toSite?.name ?? '—'}`}</LibraryRecordCardDetail>
            <LibraryRecordCardDetail muted>
              {formatDayRange(row.shipDay, row.arriveDay) ?? 'No date'}
              {row.daysLate ? <> <LateDate daysLate={row.daysLate} /></> : null}
            </LibraryRecordCardDetail>
            {React.isValidElement(contents.sub) ? <LibraryRecordCardDetail muted>{contents.sub}</LibraryRecordCardDetail> : null}
          </>
        )}
        meta={[contents.main, row.event?.code ?? null]}
        actions={renderMenu(row)}
      />
    );
  };

  const siteControl = (
    <SearchableMultiSelect
      options={context.siteOptions}
      selected={config.sites}
      onChange={state.setSites}
      placeholder="All sites"
      ariaLabel="Filter by site"
      className="events-site-filter"
      selectionSummary={summarizeSiteFilterSelection(config.sites, context.siteOptions, 'All sites')}
      showPills={false}
    />
  );
  const statusControl = (
    <StageFilterToolbar
      selectedStatuses={state.selectedStatusSet}
      onSelectedStatusesChange={state.setStatuses}
      stageGroups={STATUS_MODEL.stageGroups}
      standaloneStatuses={[]}
      orderStageLookup={STATUS_MODEL.lookup}
      defaultSelectedStatuses={STATUS_MODEL.defaults}
      label="Status"
      pluralNoun="statuses"
      allLabel="All statuses"
      defaultSelectionLabel="Open"
      compact
    />
  );

  return (
    <>
      <div className="settings-page__pinned">
        <WarehouseHero
          isPhone={context.isPhone}
          statsLabel="Transfer summary"
          stats={heroStats}
          actions={(
            <>
              <button type="button" className="btn btn-primary" onClick={onNewTransfer}>New transfer</button>
              {onScanToReceive ? <button type="button" className="btn" onClick={onScanToReceive}>Scan to receive</button> : null}
            </>
          )}
        />
        {context.tabStrip}
        <LibraryListToolbar
          search={config.search}
          onSearchChange={(value) => update({ search: value })}
          searchPlaceholder="Transfer #, description, item, SKU or serial"
          searchPlaceholderShort="Transfer #, item or SKU"
          searchLabel="Search transfers"
          siteControl={siteControl}
          siteFilterActive={state.siteFilterActive}
          facetLabel="Direction"
          facetOptions={FACET_OPTIONS}
          facet={config.facet}
          onFacetChange={(value) => update({ facet: value })}
          statusControl={statusControl}
          statusFilterActive={state.statusFilterActive}
          quickFilters={[
            { key: 'past_ship_date', label: 'Past ship date', count: chipCounts.past_ship_date ?? null, active: pastShipChip, tone: 'warn' },
            { key: 'arriving_today', label: 'Arriving today', count: chipCounts.arriving_today ?? null, active: config.chips.includes('arriving_today') },
            { key: 'linked_to_event', label: 'Linked to an event', count: null, active: config.chips.includes('linked_to_event') },
          ]}
          onToggleQuickFilter={state.toggleChip}
          extraChip={context.linkedEvent ? {
            label: `Linked to ${context.linkedEvent.code ?? 'event'}`,
            onClear: context.onClearLinkedEvent,
          } : null}
          countText={countText}
          viewOptions={VIEW_OPTIONS}
          view={config.view}
          onViewChange={(view) => update({ view })}
          viewAriaLabel="Warehouse view"
          advancedOpen={advancedOpen}
          onToggleAdvanced={() => setAdvancedOpen((open) => !open)}
          advancedActiveCount={state.advancedActiveCount}
          advancedFields={(
            <>
              <div className="library-toolbar__field text-xxs fg-muted">
                <span>Ship date</span>
                <div className="library-toolbar__range">
                  <input type="date" aria-label="Ship date from" value={config.dateFrom} onChange={(event) => update({ dateFrom: event.target.value })} />
                  <span aria-hidden="true">to</span>
                  <input type="date" aria-label="Ship date to" value={config.dateTo} onChange={(event) => update({ dateTo: event.target.value })} />
                </div>
              </div>
              <div className="library-toolbar__field text-xxs fg-muted">
                <span>Created</span>
                <div className="library-toolbar__range">
                  <input type="date" aria-label="Created from" value={config.secondDateFrom} onChange={(event) => update({ secondDateFrom: event.target.value })} />
                  <span aria-hidden="true">to</span>
                  <input type="date" aria-label="Created to" value={config.secondDateTo} onChange={(event) => update({ secondDateTo: event.target.value })} />
                </div>
              </div>
              <label className="library-toolbar__field text-xxs fg-muted">
                <span>Grouping</span>
                <select className="xs" value={config.grouping} onChange={handleGroupingChange} aria-label="Group rows by">
                  <option value="off">Off</option>
                  <option value="date">Ship date</option>
                  <option value="status">Status</option>
                  <option value="site">Site</option>
                </select>
              </label>
              <div className="library-toolbar__field text-xxs fg-muted warehouse-fold-toggle">
                <span>Stale R2 transfers</span>
                <label className="warehouse-fold-toggle__control">
                  <input type="checkbox" checked={config.fold} onChange={(event) => update({ fold: event.target.checked })} />
                  Fold confirmed R2 transfers past their ship date
                </label>
              </div>
            </>
          )}
          sortOptions={SORT_OPTIONS}
          sortKey={config.sortKey}
          sortDir={config.sortDir}
          onSortChange={handleSortChange}
          savedViews={state.savedViews}
          onResetAdvanced={state.resetAdvanced}
          onResetAll={state.resetAll}
          compact={context.compactToolbar}
          sheetResultLabel={loading ? 'Show transfers' : `Show ${formatCount(total)} ${total === 1 ? 'transfer' : 'transfers'}`}
        />
      </div>

      <main className="settings-content warehouse-content">
        <LibraryListBody
          panelId={context.panelId}
          labelledBy={context.tabElementId('transfers')}
          noun={NOUN}
          view={config.view}
          isPhone={context.isPhone}
          data={data}
          loading={loading}
          error={state.noStatuses ? null : pageQuery.error}
          onRetry={() => { void pageQuery.refetch(); }}
          blockedMessage={state.noStatuses ? 'Pick at least one status to see transfers.' : null}
          columns={columns}
          grouped={config.grouping !== 'off'}
          renderCard={renderCard}
          fold={foldCopy}
          onToggleFold={state.toggleFolded}
          emptyTitle="No transfers match these filters"
          emptyHint="Adjust the search or filters, or start a new transfer."
          page={page}
          pageSize={WAREHOUSE_PAGE_SIZE}
          onPageChange={setPage}
          pageNote={`sorted by ${(SORT_OPTIONS.find((option) => option.value === config.sortKey)?.label ?? config.sortKey).toLowerCase()}`}
          calendar={isCalendar && !state.noStatuses ? <TransfersCalendarView params={params} /> : undefined}
        />
      </main>
    </>
  );
}
